
import { DEFAULT_SHIPPING_RATES, normalizeShippingRates, ShippingRates } from '../shipping';
import { supabase } from '../supabase';
import { SiteConfig, InfoPagesConfig, CompanySettings } from '../../types';
import { uploadFile } from './shared';

export const DEFAULT_COMPANY_SETTINGS: CompanySettings = {
    name: "CoffeeNova", // Changed from CoffeeNova AB to be generic
    orgNumber: "",
    email: "info@coffeenova.se",
    phone: "",
    street: "",
    zip: "",
    city: "",
    logoUrl: "https://safpfosbbrqmexlhjrth.supabase.co/storage/v1/object/public/products/logo-1767917522580-hccoe5akwhm.png",
    bankGiro: "", 
    swish: ""
};

export const DEFAULT_LANDING_CONFIG: SiteConfig = {
    id: 'landing_page',
    theme: {
        primary: '#1A120B',
        secondary: '#816358',
        accent: '#D8C8C0',
        background: '#FDFBF9',
        surface: '#FFFFFF',
        heroOverlayOpacity: 60,
        styleMode: 'modern'
    },
    sections: [
        {
            id: 'hero',
            type: 'HERO',
            title: "Tjäna pengar till klasskassan med Sveriges godaste kaffe.",
            text: "Inga utlägg. Inget krångel. Varje säljare får en egen webbshop och ni behåller 50% av vinsten. Starta försäljningen på 2 minuter.",
            imageUrl: "https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&q=80&w=2000",
            buttons: [
                { label: "Starta Försäljning", action: "REGISTER_GROUP", primary: true },
                { label: "Hur fungerar det?", action: "SCROLL_FEATURES", primary: false }
            ],
            heroHeight: 'medium'
        },
        {
            id: 'features',
            type: 'FEATURES',
            title: "Varför CoffeeNova?",
            text: "Det smarta sättet att nå era mål.",
            features: [
                { icon: 'Zap', title: 'Helt digitalt', text: 'Slipp papperslappar och kontanthantering. Dela din unika länk via SMS eller sociala medier.' },
                { icon: 'PiggyBank', title: 'Hög vinst', text: 'Ni behåller en stor del av kakan. Pengarna går direkt till er resa eller cup.' },
                { icon: 'Coffee', title: 'En produkt folk vill ha', text: 'Handrostat premiumkaffe som smakar skillnad. Lättsålt och uppskattat.' }
            ]
        },
        {
            id: 'showcase',
            type: 'CONTENT',
            title: "Inte vilket kaffe som helst.",
            text: "Våra bönor är noga utvalda och rostade för den perfekta balansen. En produkt ni kan stå för och som era kunder kommer vilja köpa igen.",
            imageUrl: "https://images.unsplash.com/photo-1511920170033-f8396924c348?auto=format&fit=crop&q=80&w=800",
            layout: "image-left"
        }
    ],
    footer: {
        tagline: "Premiumkaffe för grupper och föreningar som vill tjäna pengar till kassan.",
        contact: {
            email: "info@coffeenova.se",
            phone: "0700-80 93 31",
            address: "Störtloppsvägen 11, 564 34 Bankeryd"
        },
        socials: {
            instagram: "https://www.instagram.com/coffeenova_group/",
            facebook: "https://www.facebook.com/profile.php?id=61586819474168",
            tiktok: "https://www.tiktok.com/@coffeenova_group"
        },
        columns: [
            {
                id: 'company',
                title: 'Om Oss',
                links: [
                    { label: 'Vårt Kaffe', url: '/#/info/coffee' },
                    { label: 'Om CoffeeNova', url: '/#/info/about' }
                ]
            },
            {
                id: 'support',
                title: 'Support',
                links: [
                    { label: 'Vanliga frågor', url: '/#/info/faq' },
                    { label: 'Köpvillkor', url: '/#/info/terms' },
                    { label: 'Integritetspolicy', url: '/#/info/privacy' },
                    { label: 'Spåra order', url: '/#/my-orders' }
                ]
            }
        ]
    }
};

export const DEFAULT_INFO_PAGES: InfoPagesConfig = {
    about: { title: "Om CoffeeNova", subtitle: "Mer än bara kaffe.", content: "" },
    coffee: { title: "Vårt Kaffe", subtitle: "Handplockat.", content: "" },
    terms: { title: "Köpvillkor", subtitle: "Tryggt.", content: "" },
    faq: { title: "Vanliga frågor", subtitle: "Svar.", content: "" },
    privacy: { 
        title: "Integritetspolicy", 
        subtitle: "Hur vi hanterar dina uppgifter", 
        // Reservtext om CMS:et inte svarar — den fullständiga policyn ligger i databasen (info_pages).
        content: "<p>Vi behandlar namn, e-post, telefonnummer och orderuppgifter för att kunna genomföra ditt köp. Vi använder bara nödvändiga cookies och ingen spårning. Kontakta info@coffeenova.se för frågor om dina personuppgifter.</p>" 
    }
};

/**
 * `strict` används av Studio: ett nätverks- eller behörighetsfel kastas i
 * stället för att ersättas med standardinnehåll, så att ingen av misstag
 * sparar standardtexter över det riktiga innehållet.
 */
export const getSiteConfig = async (key: string = 'landing_page', opts: { strict?: boolean } = {}): Promise<SiteConfig> => {
    // 1. Fetch from Network
    const { data, error } = await supabase
        .from('site_content')
        .select('*')
        .eq('key', key)
        .maybeSingle();

    if (error && error.code !== 'PGRST116') {
        if (opts.strict) throw error;
        console.error('CMS Error:', error);
    }

    // 2. Prepare Result
    let result: SiteConfig;

    if (!data) {
        result = key === 'store_page' ? { ...DEFAULT_LANDING_CONFIG, id: 'store_page', sections: [] } : DEFAULT_LANDING_CONFIG;
    } else {
        result = {
            id: data.key,
            theme: data.theme,
            sections: data.sections || [],
            footer: data.footer || DEFAULT_LANDING_CONFIG.footer
        };
    }

    // 3. Cache Result to LocalStorage for Instant Load next time
    try {
        localStorage.setItem(`cn_config_${key}`, JSON.stringify(result));
    } catch (e) {
        // Ignore storage errors (quota etc)
    }

    return result;
};

export const getInfoPagesConfig = async (opts: { strict?: boolean } = {}): Promise<InfoPagesConfig> => {
    const { data, error } = await supabase
        .from('site_content')
        .select('*')
        .eq('key', 'info_pages')
        .maybeSingle();
    if (error && opts.strict) throw error;
    
    let result = DEFAULT_INFO_PAGES;
    if (data && data.data) {
        result = data.data as InfoPagesConfig;
    }

    // Cache to LocalStorage
    try {
        localStorage.setItem('cn_info_pages', JSON.stringify(result));
    } catch (e) {}

    return result;
};

export const getCompanySettings = async (opts: { strict?: boolean } = {}): Promise<CompanySettings> => {
    const { data, error } = await supabase
        .from('site_content')
        .select('*')
        .eq('key', 'company_settings')
        .maybeSingle();
    if (error && opts.strict) throw error;
    
    let result = DEFAULT_COMPANY_SETTINGS;
    if (data && data.data) {
        result = data.data as CompanySettings;
    }

    // Cache to LocalStorage
    try {
        localStorage.setItem('cn_company_settings', JSON.stringify(result));
    } catch (e) {}

    return result;
};

export const saveSiteConfig = async (config: SiteConfig) => {
    const { error } = await supabase.from('site_content').upsert({
        key: config.id || 'landing_page',
        theme: config.theme,
        sections: config.sections,
        footer: config.footer,
        updated_at: new Date().toISOString()
    });
    if (error) throw error;
};

export const saveInfoPagesConfig = async (config: InfoPagesConfig) => {
    const { error } = await supabase.from('site_content').upsert({
        key: 'info_pages',
        data: config,
        updated_at: new Date().toISOString()
    });
    if (error) throw error;
};

export const saveCompanySettings = async (settings: CompanySettings) => {
    const { error } = await supabase.from('site_content').upsert({
        key: 'company_settings',
        data: settings,
        updated_at: new Date().toISOString()
    });
    if (error) throw error;
};

export const uploadSiteAsset = async (file: File): Promise<string> => {
    return uploadFile('products', file);
};

/** Fraktpriserna för hemleverans. Läses av kassan och av databasen (calculate_shipping). */
export const getShippingRates = async (): Promise<ShippingRates> => {
    const { data, error } = await supabase.from('site_content').select('data').eq('key', 'shipping_rates').maybeSingle();
    if (error || !data) return { ...DEFAULT_SHIPPING_RATES, enabled: false };
    return normalizeShippingRates(data.data);
};

export const saveShippingRates = async (rates: ShippingRates) => {
    const { error } = await supabase.from('site_content').upsert({
        key: 'shipping_rates',
        data: normalizeShippingRates(rates),
        updated_at: new Date().toISOString()
    });
    if (error) throw error;
};
